import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ServiceAccordion } from './ServiceAccordion';
import type { DisplayServiceWithWorkflows } from './types';

interface ServicesAccordionListProps {
  /** Display services with workflow metadata (source of truth from API) */
  services: DisplayServiceWithWorkflows[];
  /** Toggle service on/off */
  onServiceToggle: (serviceId: string, active: boolean) => void | Promise<void>;
  /** Toggle workflow on/off */
  onWorkflowToggle: (
    serviceId: string,
    workflowCode: string,
    enabled: boolean,
  ) => void | Promise<void>;
  /** Update workflow notes - async to support API calls */
  onWorkflowNotesChange: (
    serviceId: string,
    workflowCode: string,
    notes: string,
  ) => Promise<void>;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Optional function to check if a specific service can be edited */
  canEditService?: (serviceId: string) => boolean;
  /** Start with all sections collapsed (default: false, auto-expands active services) */
  defaultCollapsed?: boolean;
}

/**
 * Presentational component for rendering the services accordion list.
 *
 * Architecture aligned with firm services:
 * - Uses services from props as source of truth (from RTK Query cache)
 * - Manages only the accordion expansion state locally
 * - Notes editing is handled by WorkflowToggleRow with async save
 */
export const ServicesAccordionList: React.FC<ServicesAccordionListProps> = ({
  services,
  onServiceToggle,
  onWorkflowToggle,
  onWorkflowNotesChange,
  disabled = false,
  canEditService,
  defaultCollapsed = false,
}) => {
  // Track which accordions are expanded.
  // Lazy init handles synchronous case; useEffect handles async data arrival.
  const [expandedServices, setExpandedServices] = useState<Set<string>>(() => {
    if (defaultCollapsed || services.length === 0) {
      return new Set<string>();
    }
    return new Set(
      services.filter((s) => s.is_active).map((s) => s.service_id),
    );
  });

  // Track if we've initialized expanded state after async data arrives
  const hasInitializedRef = useRef(services.length > 0);

  // Initialize expanded state when services first becomes available (async case).
  // This is necessary because services may be empty on initial render when fetching.
  useEffect(() => {
    if (!hasInitializedRef.current && services.length > 0) {
      if (!defaultCollapsed) {
        setExpandedServices(
          new Set(services.filter((s) => s.is_active).map((s) => s.service_id)),
        );
      }
      hasInitializedRef.current = true;
    }
  }, [services, defaultCollapsed]);

  // Toggle accordion expansion
  const handleExpandedChange = useCallback(
    (serviceId: string, expanded: boolean) => {
      setExpandedServices((prev) => {
        const next = new Set(prev);
        if (expanded) {
          next.add(serviceId);
        } else {
          next.delete(serviceId);
        }
        return next;
      });
    },
    [],
  );

  // Wrap service toggle to also handle expansion
  const handleServiceToggle = useCallback(
    (serviceId: string, active: boolean) => {
      onServiceToggle(serviceId, active);
      // Auto-expand when turning on
      if (active) {
        setExpandedServices((prev) => new Set(prev).add(serviceId));
      }
    },
    [onServiceToggle],
  );

  if (services.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {services.map((service) => {
        // Check if this specific service can be edited
        const isServiceEditable = canEditService
          ? canEditService(service.service_id)
          : true;

        return (
          <ServiceAccordion
            key={service.service_id}
            service={service}
            isExpanded={expandedServices.has(service.service_id)}
            onExpandedChange={(expanded) =>
              handleExpandedChange(service.service_id, expanded)
            }
            onServiceToggle={(active) =>
              handleServiceToggle(service.service_id, active)
            }
            onWorkflowToggle={(workflowCode, enabled) =>
              onWorkflowToggle(service.service_id, workflowCode, enabled)
            }
            onWorkflowNotesChange={(workflowCode, notes) =>
              onWorkflowNotesChange(service.service_id, workflowCode, notes)
            }
            disabled={disabled || !isServiceEditable}
            notConfigured={!isServiceEditable}
          />
        );
      })}
    </div>
  );
};
